import { Role, rolesManipulator } from 'auth';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { useGetRegisterIdParam } from '@/modules/common/hooks/useGetRegisterIdParam.ts';
import { useRegisterPermissionsQuery } from '@/modules/common/queries/permissions.ts';
import HolderSearch from '@/modules/issuer-dashboard/components/HolderSearch.tsx';
import IssuedCapitalChart from '@/modules/issuer-dashboard/components/IssuedCapitalChart.tsx';
import TopHoldersChart from '@/modules/issuer-dashboard/components/TopHoldersChart.tsx';
import EditRequestsTab from '@/modules/issuer-dashboard/components/holder-create/EditRequestsTab.tsx';
import CommonReports from '@/modules/issuer-dashboard/components/holders/CommonReports.tsx';
import CommunicationPreference from '@/modules/issuer-dashboard/components/holders/CommunicationPreference.tsx';
import PaymentElection from '@/modules/issuer-dashboard/components/holders/PaymentElection.tsx';
import { HeaderHolders } from '@/modules/issuer-dashboard/pages/IssuerHoldersNew.tsx';
import { useIssuerEntitiesQuery } from '@/modules/issuer-dashboard/queries/entities.ts';
import { useIssuerGetInvestorsEditListQuery } from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { useRegisterShareholdersQuery } from '@/modules/issuer-dashboard/queries/shareholders.tsx';
import { Badge, InfoCard, PageLayout, TabItem, Tabs } from 'ui';
import { EntitiesPaymentInfoStatus, EntityStatus, InvestorEditsStatus } from 'api';

export enum HoldersTabNames {
  Overview = 'Overview',
  EditRequests = 'Pending requests',
}

export const HOLDERS_TAB_NAME = 'tab-holders';

const IssuerHolders = () => {
  const [params] = useSearchParams();
  const registerId = useGetRegisterIdParam();
  const isIssuerAdmin = rolesManipulator.current_user_role === Role.SR_ISSUER_ADMIN;
  const { data: dataRegisterPermissions } = useRegisterPermissionsQuery();
  const { data: dataPendingHolders } = useIssuerEntitiesQuery(
    {
      page: 1,
      page_size: 1,
      status: EntityStatus.EntityAwaitingApproval,
    },
    !isIssuerAdmin || !dataRegisterPermissions?.holder_details_permission,
  );
  const { data: dataPendingInvestorEdits } = useIssuerGetInvestorsEditListQuery(
    {
      page: 1,
      page_size: 1,
      status: [InvestorEditsStatus.Pending, InvestorEditsStatus.InProgress].toString(),
    },
    !dataRegisterPermissions?.holder_details_permission,
  );
  const { data: dataRegisterShareholders } = useRegisterShareholdersQuery(
    { register_id: Number(registerId || 0) },
    !registerId || !dataRegisterPermissions?.holder_details_permission,
  );
  const paramTab = params.get(HOLDERS_TAB_NAME);
  const isValidTab =
    paramTab && Object.values(HoldersTabNames).includes(paramTab as HoldersTabNames);
  const [activeTab, setActiveTab] = useState<HoldersTabNames>(
    isValidTab ? (paramTab as HoldersTabNames) : HoldersTabNames.Overview,
  );

  useEffect(() => {
    const isValidTab =
      paramTab && Object.values(HoldersTabNames).includes(paramTab as HoldersTabNames);
    setActiveTab(isValidTab ? (paramTab as HoldersTabNames) : HoldersTabNames.Overview);
  }, [paramTab]);

  const bankInfoRequestsCount = useMemo(
    () =>
      (dataRegisterShareholders?.shareholders || []).filter(
        shareholder =>
          shareholder.entity?.payment_info_status === EntitiesPaymentInfoStatus.Unverified &&
          shareholder.entity?.payment_info_requested_at,
      ).length,
    [dataRegisterShareholders?.shareholders],
  );

  const pendingRequestsCount =
    (isIssuerAdmin ? dataPendingHolders?.pages?.[0]?.total ?? 0 : 0) +
    (dataPendingInvestorEdits?.pages?.[0]?.total ?? 0) +
    bankInfoRequestsCount;

  const pendingRequestsLabel = (
    <span className="flex items-center gap-2">
      <span>{HoldersTabNames.EditRequests}</span>
      {pendingRequestsCount > 0 && (
        <Badge containerClass="bg-blue-700 text-white text-xs py-0 px-2 min-h-5">
          {pendingRequestsCount}
        </Badge>
      )}
    </span>
  );

  const tabItems = useMemo<TabItem<HoldersTabNames>[]>(() => {
    return [
      {
        name: HoldersTabNames.Overview,
        container: <OverviewTab />,
      },
      {
        name: HoldersTabNames.EditRequests,
        label: pendingRequestsLabel,
        container: <EditRequestsTab />,
        disabled: !dataRegisterPermissions?.holder_details_permission,
        disabledReason: 'This feature is not available for this issuer',
      },
    ];
  }, [dataRegisterPermissions?.holder_details_permission, pendingRequestsLabel]);

  return (
    <PageLayout
      title="Holders"
      headerAction={<HeaderHolders />}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      tabNameParam={HOLDERS_TAB_NAME}
      tabs={tabItems}
    >
      <Tabs items={tabItems} activeTab={activeTab} />
    </PageLayout>
  );
};

const OverviewTab = () => {
  return (
    <div className="flex flex-col gap-6">
      <InfoCard title="Find holder by name or ID">
        <HolderSearch />
      </InfoCard>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <CommonReports />
        <TopHoldersChart title="Top holders" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="flex flex-col gap-6">
          <CommunicationPreference />
          <PaymentElection />
        </div>
        <IssuedCapitalChart />
      </div>
    </div>
  );
};

export default withPrivateAccess(IssuerHolders, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
